import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { API_BASE } from '../../config';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { SuggestionDetailAdminModal } from './SuggestionDetailAdminModal';
import { AdminAccessDenied } from './AdminAccessDenied';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  ADMIN_BADGE_DANGER_CLASSNAME,
  ADMIN_BADGE_NEUTRAL_CLASSNAME,
  ADMIN_BADGE_SUCCESS_CLASSNAME,
  ADMIN_BADGE_WARNING_CLASSNAME,
  ADMIN_CHART_PANEL_CLASSNAME,
  ADMIN_CHART_TOOLTIP_STYLE,
  ADMIN_EMPTY_BOX_CLASSNAME,
  ADMIN_FIELD_LABEL_CLASSNAME,
  ADMIN_FILTER_CONTROL_CLASSNAME,
  ADMIN_FILTERS_GRID_CLASSNAME,
  ADMIN_MUTED_TEXT_CLASSNAME,
  ADMIN_PAGE_CLASSNAME,
  ADMIN_PANEL_CLASSNAME,
  ADMIN_PANEL_TITLE_CLASSNAME,
  ADMIN_STAT_CARD_CLASSNAME,
  ADMIN_STAT_LABEL_CLASSNAME,
  ADMIN_STAT_VALUE_CLASSNAME,
  ADMIN_STATS_ROW_CLASSNAME,
  AdminEmptyState,
  AdminErrorAlert,
  AdminLoadingState,
  AdminPaginationBar,
  AdminTableWrap,
} from './admin-ui';

interface LLMCall {
  id: string;
  userId: string | null;
  documentId: string | null;
  triggerSource: string;
  provider: 'gemini' | 'openai';
  model: string;
  prompt: string;
  response: string | null;
  promptTokens: number | null;
  responseTokens: number | null;
  costUsd: number | null;
  durationSeconds: number | null;
  createdAt: string;
}

interface Suggestion {
  id: string;
  userId: string;
  userEmail?: string;
  documentId: string | null;
  originalText: string;
  suggestionText: string;
  issueType: 'orthographic' | 'preciseness' | 'clarity' | 'consistency' | 'orthographic_consistency' | 'grammar' | 'spelling' | 'punctuation' | 'sentence_structure' | 'repetition' | 'overused_words' | 'cacophony' | 'passive_voice' | 'show_vs_tell' | 'pacing' | 'adverbs' | 'adjectives' | 'cliches' | 'transitions' | 'style' | 'strength' | 'user_edit';
  feedbackText: string;
  llmCallId: string | null;
  llmCall?: LLMCall | null;
  languageCode: string | null;
  languageName: string | null;
  shownAt: string;
  status: 'pending' | 'applied' | 'rejected' | 'reported' | 'error:originalTextNotFound';
  closedAt: string | null;
  createdAt: string;
}

export const Suggestions: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [globalLlmModel, setGlobalLlmModel] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [aggregates, setAggregates] = useState<{
    total: number;
    applied: number;
    appliedRate: number;
    modelDistribution: Array<{ name: string; value: number }>;
    issueTypeDistribution: Array<{ name: string; value: number; originalName: string }>;
    statusDistribution: Array<{ name: string; value: number; originalName: string }>;
  } | null>(null);
  const [filters, setFilters] = useState<{
    shownAtFrom: string;
    shownAtTo: string;
    status: string;
    issueType: string;
    model: string;
    provider: string;
  }>({
    shownAtFrom: '',
    shownAtTo: '',
    status: '',
    issueType: '',
    model: '',
    provider: '',
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [filters.shownAtFrom, filters.shownAtTo, filters.status, filters.issueType, filters.model, filters.provider]);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (masterKey) {
          params.append('masterKey', masterKey);
        }
        if (filters.shownAtFrom) {
          const dateFrom = new Date(filters.shownAtFrom);
          params.append('shownAtFrom', dateFrom.toISOString());
        }
        if (filters.shownAtTo) {
          const dateTo = new Date(filters.shownAtTo);
          params.append('shownAtTo', dateTo.toISOString());
        }
        if (filters.status) {
          params.append('status', filters.status);
        }
        if (filters.issueType) {
          params.append('issueType', filters.issueType);
        }
        if (filters.model) {
          params.append('model', filters.model);
        }
        if (filters.provider) {
          params.append('provider', filters.provider);
        }
        params.append('page', currentPage.toString());
        params.append('pageSize', '25');

        const url = `${API_BASE}/api/suggestions${params.toString() ? `?${params.toString()}` : ''}`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch suggestions');
        }

        const data = await response.json();
        setSuggestions(data.suggestions || []);
        setTotalPages(data.pagination?.totalPages || 1);
        setTotalCount(data.pagination?.total || 0);
        setAggregates(data.aggregates || null);
        setError(null);
      } catch (err) {
        console.error('Error fetching suggestions:', err);
        setError('Error al cargar sugerencias');
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
  }, [token, user?.admin, masterKey, currentPage, filters.shownAtFrom, filters.shownAtTo, filters.status, filters.issueType, filters.model, filters.provider]);

  useEffect(() => {
    const fetchModels = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        return;
      }

      try {
        const url = masterKey
          ? `${API_BASE}/api/suggestions/models?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/suggestions/models`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (response.ok) {
          const data = await response.json();
          setAvailableModels(data.models || []);
        }
      } catch (err) {
        console.error('Error fetching models:', err);
      }
    };

    fetchModels();
  }, [token, user, masterKey]);

  useEffect(() => {
    const fetchStatuses = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        return;
      }

      try {
        const url = masterKey
          ? `${API_BASE}/api/suggestions/statuses?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/suggestions/statuses`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (response.ok) {
          const data = await response.json();
          setAvailableStatuses(data.statuses || []);
        }
      } catch (err) {
        console.error('Error fetching statuses:', err);
      }
    };

    fetchStatuses();
  }, [token, user, masterKey]);

  useEffect(() => {
    const fetchGlobalLlmSettings = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        return;
      }

      try {
        const url = masterKey
          ? `${API_BASE}/api/admin-settings/llm?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/admin-settings/llm`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (response.ok) {
          const data = await response.json();
          const globalModel = data.globalLlmModel || '';
          setGlobalLlmModel(globalModel);
          setFilters((prev) => {
            if (globalModel && !prev.model) {
              return { ...prev, model: globalModel };
            }
            return prev;
          });
        }
      } catch (err) {
        console.error('Error fetching global LLM settings:', err);
      }
    };

    fetchGlobalLlmSettings();
  }, [token, user, masterKey]);

  const getIssueTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      orthographic: 'Ortográfico',
      preciseness: 'Precisión',
      clarity: 'Claridad',
      consistency: 'Consistencia',
      orthographic_consistency: 'Consistencia Ortográfica',
      grammar: 'Gramática',
      spelling: 'Ortografía',
      punctuation: 'Puntuación',
      sentence_structure: 'Estructura de Oración',
      repetition: 'Repetición',
      overused_words: 'Palabras Sobreutilizadas',
      cacophony: 'Cacofonía',
      passive_voice: 'Voz Pasiva',
      show_vs_tell: 'Mostrar vs Contar',
      pacing: 'Ritmo',
      adverbs: 'Adverbios',
      adjectives: 'Adjetivos',
      cliches: 'Clichés',
      transitions: 'Transiciones',
      style: 'Estilo',
      strength: 'Fortaleza',
      other: 'Otro',
    };
    return labels[type] || type;
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      pending: 'Pendiente',
      applied: 'Aprobada',
      rejected: 'Rechazada',
      reported: 'Reportada',
      'error:originalTextNotFound': 'Error: Texto original no encontrado',
    };
    return labels[status] || status;
  };

  const getStatusBadgeClassName = (status: string): string => {
    switch (status) {
      case 'applied':
        return ADMIN_BADGE_SUCCESS_CLASSNAME;
      case 'rejected':
      case 'error:originalTextNotFound':
        return ADMIN_BADGE_DANGER_CLASSNAME;
      case 'reported':
        return ADMIN_BADGE_WARNING_CLASSNAME;
      default:
        return ADMIN_BADGE_NEUTRAL_CLASSNAME;
    }
  };

  const getIssueTypeBadgeClassName = (issueType: string): string => {
    if (issueType === 'strength') return ADMIN_BADGE_SUCCESS_CLASSNAME;
    if (issueType === 'orthographic') return ADMIN_BADGE_DANGER_CLASSNAME;
    return ADMIN_BADGE_WARNING_CLASSNAME;
  };

  const chartData = useMemo(() => {
    if (!aggregates) {
      return {
        modelData: [],
        issueTypeData: [],
        statusData: [],
        modelDataForStatus: [],
      };
    }

    const modelData = aggregates.modelDistribution.map((item) => ({
      name: item.name,
      value: item.value,
    }));

    const issueTypeData = aggregates.issueTypeDistribution.map((item) => ({
      name: getIssueTypeLabel(item.originalName),
      value: item.value,
      originalName: item.originalName,
    }));

    const statusData = aggregates.statusDistribution.map((item) => ({
      name: getStatusLabel(item.originalName),
      value: item.value,
      originalName: item.originalName,
    }));

    const modelDataForStatus = aggregates.modelDistribution.map((item) => ({
      name: item.name,
      value: item.value,
    }));

    return { modelData, issueTypeData, statusData, modelDataForStatus };
  }, [aggregates]);

  const COLORS_MODELS = ['var(--scribane-black)', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];
  const COLORS_ISSUE_TYPES = ['#fca5a5', '#fcd34d', '#93c5fd', '#a5b4fc', '#f9a8d4', '#6ee7b7', '#d1d5db'];
  const COLORS_STATUS = ['#d1fae5', '#fee2e2', '#fef3c7', '#f3f4f6'];

  const getCurrentIndex = useCallback((): number => {
    if (!selectedSuggestion) return -1;
    return suggestions.findIndex(s => s.id === selectedSuggestion.id);
  }, [selectedSuggestion, suggestions]);

  const navigateToSuggestion = useCallback((direction: 'prev' | 'next'): void => {
    const currentIndex = getCurrentIndex();
    
    if (currentIndex === -1) return;

    let targetIndex: number;
    if (direction === 'prev') {
      targetIndex = currentIndex - 1;
    } else {
      targetIndex = currentIndex + 1;
    }

    if (targetIndex < 0 || targetIndex >= suggestions.length) return;

    setSelectedSuggestion(suggestions[targetIndex]);
  }, [getCurrentIndex, suggestions]);

  const currentIndex = getCurrentIndex();
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < suggestions.length - 1;

  useEffect(() => {
    if (!selectedSuggestion) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) {
        return;
      }

      if (e.key === 'ArrowLeft' && hasPrevious) {
        e.preventDefault();
        navigateToSuggestion('prev');
      } else if (e.key === 'ArrowRight' && hasNext) {
        e.preventDefault();
        navigateToSuggestion('next');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedSuggestion, hasPrevious, hasNext, navigateToSuggestion]);

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  if (loading) {
    return <AdminLoadingState />;
  }

  const appliedCount = aggregates?.applied || 0;
  const totalCountForDisplay = aggregates?.total || 0;
  const appliedRate = aggregates?.appliedRate ? aggregates.appliedRate.toFixed(1) : '0';

  return (
    <>
      <div className={ADMIN_PAGE_CLASSNAME}>
        {error && (
          <div className="mb-8">
            <AdminErrorAlert>{error}</AdminErrorAlert>
          </div>
        )}

        <div className={ADMIN_PANEL_CLASSNAME}>
          <h2 className={ADMIN_PANEL_TITLE_CLASSNAME}>
            Filtros
          </h2>
          <div className={ADMIN_FILTERS_GRID_CLASSNAME}>
            <div>
              <label className={ADMIN_FIELD_LABEL_CLASSNAME}>
                Fecha desde
              </label>
              <Input
                type="datetime-local"
                value={filters.shownAtFrom}
                onChange={(e) => setFilters({ ...filters, shownAtFrom: e.target.value })}
                className={ADMIN_FILTER_CONTROL_CLASSNAME}
              />
            </div>
            <div>
              <label className={ADMIN_FIELD_LABEL_CLASSNAME}>
                Fecha hasta
              </label>
              <Input
                type="datetime-local"
                value={filters.shownAtTo}
                onChange={(e) => setFilters({ ...filters, shownAtTo: e.target.value })}
                className={ADMIN_FILTER_CONTROL_CLASSNAME}
              />
            </div>
            <div>
              <label className={ADMIN_FIELD_LABEL_CLASSNAME}>
                Estado
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className={ADMIN_FILTER_CONTROL_CLASSNAME}
              >
                <option value="">Todos</option>
                {availableStatuses.map((status) => (
                  <option key={status} value={status}>
                    {getStatusLabel(status)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={ADMIN_FIELD_LABEL_CLASSNAME}>
                Tipo de sugerencia
              </label>
              <select
                value={filters.issueType}
                onChange={(e) => setFilters({ ...filters, issueType: e.target.value })}
                className={ADMIN_FILTER_CONTROL_CLASSNAME}
              >
                <option value="">Todos</option>
                <option value="orthographic">Ortográfico</option>
                <option value="preciseness">Precisión</option>
                <option value="clarity">Claridad</option>
                <option value="style">Estilo</option>
                <option value="consistency">Consistencia</option>
                <option value="repetition">Repetición</option>
                <option value="cacophony">Cacofonía</option>
                <option value="strength">Fortaleza</option>
                <option value="other">Otro</option>
              </select>
            </div>
            <div>
              <label className={ADMIN_FIELD_LABEL_CLASSNAME}>
                Modelo
              </label>
              <select
                value={filters.model}
                onChange={(e) => setFilters({ ...filters, model: e.target.value })}
                className={ADMIN_FILTER_CONTROL_CLASSNAME}
              >
                <option value="">Todos</option>
                {availableModels.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={ADMIN_FIELD_LABEL_CLASSNAME}>
                Proveedor
              </label>
              <select
                value={filters.provider}
                onChange={(e) => setFilters({ ...filters, provider: e.target.value })}
                className={ADMIN_FILTER_CONTROL_CLASSNAME}
              >
                <option value="">Todos</option>
                <option value="gemini">Gemini</option>
                <option value="openai">OpenAI</option>
              </select>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            className="mt-4"
            onClick={() => {
              setFilters({
                shownAtFrom: '',
                shownAtTo: '',
                status: '',
                issueType: '',
                model: globalLlmModel || '',
                provider: '',
              });
            }}
          >
            Limpiar filtros
          </Button>
        </div>

        <div className={ADMIN_STATS_ROW_CLASSNAME}>
          <div className={ADMIN_STAT_CARD_CLASSNAME}>
            <div className={`${ADMIN_STAT_LABEL_CLASSNAME} mb-2`}>Total</div>
            <div className={ADMIN_STAT_VALUE_CLASSNAME}>{totalCountForDisplay}</div>
          </div>
          <div className={ADMIN_STAT_CARD_CLASSNAME}>
            <div className={`${ADMIN_STAT_LABEL_CLASSNAME} mb-2`}>Aplicadas</div>
            <div className={`${ADMIN_STAT_VALUE_CLASSNAME} text-emerald-600 dark:text-emerald-400`}>{appliedCount}</div>
          </div>
          <div className={ADMIN_STAT_CARD_CLASSNAME}>
            <div className={`${ADMIN_STAT_LABEL_CLASSNAME} mb-2`}>Tasa de Aplicación</div>
            <div className={ADMIN_STAT_VALUE_CLASSNAME}>{appliedRate}%</div>
          </div>
        </div>

        {aggregates && aggregates.total > 0 && (chartData.modelData.length > 0 || chartData.issueTypeData.length > 0) && (
          <div className={ADMIN_PANEL_CLASSNAME}>
            <h2 className={ADMIN_PANEL_TITLE_CLASSNAME}>
              Distribución de Sugerencias
            </h2>
            <div className="flex flex-wrap items-start justify-center gap-8">
              <div className={ADMIN_CHART_PANEL_CLASSNAME}>
                <h3 className="mb-4 text-center text-base font-semibold text-foreground">
                  Por Tipo de Sugerencia
                </h3>
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Tooltip
                      {...ADMIN_CHART_TOOLTIP_STYLE}
                      formatter={(value) => [`${typeof value === 'number' ? value : 0}`, 'Cantidad']}
                      labelFormatter={(label: string) => label}
                    />
                    {!filters.issueType && (
                      <Pie
                        data={chartData.issueTypeData}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        outerRadius={140}
                        innerRadius={filters.model ? 0 : 100}
                        fill="#8884d8"
                        label={({ name, percent }) => `${name}: ${percent ? (percent * 100).toFixed(0) : 0}%`}
                      >
                        {chartData.issueTypeData.map((_, index) => (
                          <Cell key={`cell-issue-${index}`} fill={COLORS_ISSUE_TYPES[index % COLORS_ISSUE_TYPES.length]} />
                        ))}
                      </Pie>
                    )}
                    {!filters.model && (
                      <Pie
                        data={chartData.modelData}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        fill="#82ca9d"
                        label={({ name, percent }) => `${name}: ${percent ? (percent * 100).toFixed(0) : 0}%`}
                      >
                        {chartData.modelData.map((_, index) => (
                          <Cell key={`cell-model-${index}`} fill={COLORS_MODELS[index % COLORS_MODELS.length]} />
                        ))}
                      </Pie>
                    )}
                    <Legend
                      wrapperStyle={{ paddingTop: '1rem' }}
                      formatter={(value, entry: any) => {
                        if (entry.payload?.originalName) {
                          return value;
                        }
                        return value;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className={`mt-4 flex justify-center gap-8 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>
                  {!filters.model && (
                    <div className="flex items-center gap-2">
                      <div
                        className="h-4 w-4 rounded-full"
                        style={{ backgroundColor: 'var(--scribane-black)' }}
                      />
                      <span>Anillo interno: Modelo</span>
                    </div>
                  )}
                  {!filters.issueType && (
                    <div className="flex items-center gap-2">
                      <div
                        className="h-4 w-4 rounded-full"
                        style={{ backgroundColor: '#fee2e2' }}
                      />
                      <span>Anillo externo: Tipo de sugerencia</span>
                    </div>
                  )}
                </div>
              </div>
              {chartData.statusData.length > 0 && chartData.modelDataForStatus.length > 0 && (
                <div className={ADMIN_CHART_PANEL_CLASSNAME}>
                  <h3 className="mb-4 text-center text-base font-semibold text-foreground">
                    Por Estado
                  </h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Tooltip
                        {...ADMIN_CHART_TOOLTIP_STYLE}
                        formatter={(value) => [`${typeof value === 'number' ? value : 0}`, 'Cantidad']}
                        labelFormatter={(label: string) => label}
                      />
                      <Pie
                        data={chartData.statusData}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        outerRadius={140}
                        innerRadius={filters.model ? 0 : 100}
                        fill="#8884d8"
                        label={({ name, percent }) => `${name}: ${percent ? (percent * 100).toFixed(0) : 0}%`}
                      >
                        {chartData.statusData.map((entry, index) => {
                          const statusColors: Record<string, string> = {
                            pending: '#f3f4f6',
                            applied: '#d1fae5',
                            rejected: '#fee2e2',
                            reported: '#fef3c7',
                          };
                          return (
                            <Cell
                              key={`cell-status-${index}`}
                              fill={statusColors[entry.originalName] || COLORS_STATUS[index % COLORS_STATUS.length]}
                            />
                          );
                        })}
                      </Pie>
                      {!filters.model && (
                        <Pie
                          data={chartData.modelDataForStatus}
                          dataKey="value"
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          fill="#82ca9d"
                          label={({ name, percent }) => `${name}: ${percent ? (percent * 100).toFixed(0) : 0}%`}
                        >
                          {chartData.modelDataForStatus.map((_, index) => (
                            <Cell key={`cell-model-status-${index}`} fill={COLORS_MODELS[index % COLORS_MODELS.length]} />
                          ))}
                        </Pie>
                      )}
                      <Legend
                        wrapperStyle={{ paddingTop: '1rem' }}
                        formatter={(value, entry: any) => {
                          if (entry.payload?.originalName) {
                            return value;
                          }
                          return value;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className={`mt-4 flex justify-center gap-8 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>
                    {!filters.model && (
                      <div className="flex items-center gap-2">
                        <div
                          className="h-4 w-4 rounded-full"
                          style={{ backgroundColor: 'var(--scribane-black)' }}
                        />
                        <span>Anillo interno: Modelo</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <div
                        className="h-4 w-4 rounded-full"
                        style={{ backgroundColor: '#d1fae5' }}
                      />
                      <span>Anillo externo: Estado</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {suggestions.length === 0 ? (
          <div className={ADMIN_EMPTY_BOX_CLASSNAME}>
            <AdminEmptyState>No hay sugerencias registradas</AdminEmptyState>
          </div>
        ) : (
          <>
          <AdminTableWrap>
              <table className="admin-data-table" style={{ minWidth: '800px' }}>
                <thead>
                  <tr>
                    <th>
                      Usuario
                    </th>
                    <th>
                      Texto Original
                    </th>
                    <th>
                      Tipo
                    </th>
                    <th>
                      Modelo
                    </th>
                    <th>
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {suggestions.map((s) => (
                    <tr
                      key={s.id}
                      className="admin-data-table-row-clickable"
                      onClick={() => setSelectedSuggestion(s)}
                    >
                      <td>
                        {s.userEmail ? (
                          <Link
                            to={`/admin/users/${s.userId}`}
                            className="admin-table-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {s.userEmail}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="max-w-[300px]">
                        <div className="overflow-hidden text-ellipsis whitespace-nowrap" title={s.originalText}>
                          {s.originalText}
                        </div>
                      </td>
                      <td>
                        <span className={getIssueTypeBadgeClassName(s.issueType)}>
                          {getIssueTypeLabel(s.issueType)}
                        </span>
                      </td>
                      <td className="admin-table-cell-muted">
                        {s.llmCall ? s.llmCall.model : '-'}
                      </td>
                      <td>
                        <span className={getStatusBadgeClassName(s.status)}>
                          {getStatusLabel(s.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableWrap>
            <AdminPaginationBar
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalCount={totalCount}
            />
          </>
        )}
      </div>

      {selectedSuggestion && (
        <SuggestionDetailAdminModal
          suggestion={selectedSuggestion}
          onClose={() => setSelectedSuggestion(null)}
          onNavigatePrev={() => navigateToSuggestion('prev')}
          onNavigateNext={() => navigateToSuggestion('next')}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          showUserEmail={true}
          getIssueTypeLabel={getIssueTypeLabel}
          getStatusLabel={getStatusLabel}
        />
      )}
    </>
  );
};

